import { mmss } from '../circles/sessionClock';

/**
 * WHAT THE CALL'S CLOCK SAYS, AND WHEN — every threshold of the 90-minute cap,
 * in one pure function.
 *
 * THE CAP ITSELF IS NOT HERE. That is the point. The server writes
 * `empathy_sessions.ends_at` at match and sends it to both roles; everything
 * below is computed from the seconds remaining to that instant, so the client
 * has no opinion about how long a call may run and two clients cannot disagree
 * about it. `__tests__/call-cap-single-copy.test.ts` fails if a number that
 * looks like a cap reappears anywhere in `src/`.
 *
 * WHY A PURE MODULE RATHER THAN A TERNARY IN THE RENDER. The old version was
 * two constants and an inline expression inside `PeerAudioRoom`
 * (`SESSION_HARD_CAP_SECONDS`, `SESSION_WARNING_THRESHOLD_SECONDS`), which
 * meant the only way to find out what the bar says at 84 minutes was to sit in
 * a call for 84 minutes. A table test over this function asks the question in
 * a millisecond, and the thresholds are then written down exactly once.
 *
 * THE SHAPE OF THE ENDING, David 2026-09-14: count UP until a quarter of an
 * hour is left, then show what is LEFT, and at five minutes say so in words.
 * Nothing sounds, nothing is an `alert`, and there is no "extend" anywhere —
 * a contemplative conversation gets a deadline only once it genuinely has one.
 *
 * THAT SHAPE IS THE NINETY-MINUTE ONE, and it does not fit every cap. On the
 * three-minute dev cap the bar read "Wrapping up in 5 minutes" for the whole
 * call: the sentence was a constant, and the five-minute threshold was longer
 * than the call. So the thresholds are now a FUNCTION of the cap
 * (`capThresholds`) and the sentence is DERIVED from the threshold
 * (`capWrapUpLine`) rather than written beside it — a threshold that moves
 * without its copy is exactly the bug that was reported.
 */

/**
 * Below this many seconds remaining, the clock shows remaining, not elapsed.
 * The ABSOLUTE thresholds, for any cap long enough to hold this shape.
 */
export const CAP_REMAINING_THRESHOLD_SECONDS = 15 * 60;

/** Below this, the quiet line becomes the stronger "wrapping up" sentence. */
export const CAP_WRAP_UP_THRESHOLD_SECONDS = 5 * 60;

/** Under a minute, "1 min left" is a lie for 59 of those seconds. Show m:ss. */
export const CAP_SECONDS_THRESHOLD = 60;

/**
 * How far past `ends_at` a client waits for the server's `end` before ending
 * the call on its own.
 *
 * BELT AND BRACES, not a second authority (class D). The server is what ends a
 * call; this only covers the case where its message cannot arrive — a phone
 * that was asleep across the deadline, a poll that 500s, a WebSocket that
 * dropped. Ten seconds is long enough that a healthy delivery always wins the
 * race (the poll's own ceiling is 500 ms after the handshake) and short enough
 * that nobody sits in a dead call wondering.
 */
export const CAP_LOCAL_FALLBACK_SECONDS = 10;

export type CallCapPhase = 'counting-up' | 'remaining' | 'wrapping-up' | 'over';

/** The two seconds-remaining marks that separate this call's three live phases. */
export interface CapThresholds {
  /** At or below this many seconds left, the clock counts DOWN. */
  remainingAt: number;
  /** At or below this many seconds left, the "wrapping up" sentence appears. */
  wrapUpAt: number;
}

/**
 * WHERE THIS CALL'S PHASES FALL, given how long it may run.
 *
 * `capSeconds` is the span the SERVER armed (`ends_at - started_at`), handed to
 * the client with the deadline; null when it has not arrived yet or the backend
 * is older than the field. Null means the absolute thresholds — the behaviour
 * this module had before caps could be short, so a missing field degrades to
 * the old shape rather than to a guess.
 *
 * FIFTEEN MINUTES IS THE BOUNDARY because that is the count-up's own threshold:
 * a cap shorter than it has no count-up phase to offer, so the countdown runs
 * from the first second (`Infinity`, not `capSeconds` — a client clock a few
 * seconds behind the server would otherwise read "remaining > cap" at the start
 * of the call and flash the count-up for those seconds).
 *
 * THE WRAP-UP IS THE LAST THIRD of a short cap: one minute of three, two of
 * six, three of ten. Rounded to a whole minute because the sentence counts in
 * whole minutes, and floored at one because "wrapping up in 0 minutes" is not a
 * warning. It is deliberately NOT clamped to the cap: on a cap under ninety
 * seconds the last third rounds up to a minute that covers most of the call,
 * which for a call that short is the honest reading.
 */
export function capThresholds(capSeconds: number | null): CapThresholds {
  if (
    capSeconds === null ||
    !Number.isFinite(capSeconds) ||
    capSeconds >= CAP_REMAINING_THRESHOLD_SECONDS
  ) {
    return {
      remainingAt: CAP_REMAINING_THRESHOLD_SECONDS,
      wrapUpAt: CAP_WRAP_UP_THRESHOLD_SECONDS,
    };
  }

  return {
    remainingAt: Number.POSITIVE_INFINITY,
    wrapUpAt: Math.max(1, Math.round(capSeconds / 3 / 60)) * 60,
  };
}

/**
 * THE WRAP-UP SENTENCE, said from the threshold that triggers it.
 *
 * It names the minutes left AT THE CROSSING and then holds still — the clock
 * beside it is already counting the seconds down, and a sentence that counted
 * with it would be two countdowns saying the same thing a second apart.
 *
 * Singular at one minute. "Wrapping up in 1 minutes" was the first draft of
 * this and it is the kind of thing nobody reports and everybody notices.
 */
export function capWrapUpLine(wrapUpSeconds: number): string {
  const minutes = Math.max(1, Math.round(wrapUpSeconds / 60));

  return `Wrapping up in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

export interface CallCapView {
  phase: CallCapPhase;
  /** What the right-hand clock slot shows — the ONLY thing in it. */
  clockText: string;
  /**
   * Is `clockText` a CLOCK (m:ss) or a SENTENCE ("14 min left")?
   *
   * The strip sets `font-mono tabular-nums` on a clock so its digits stop
   * shuffling the layout every second, and must not on a sentence — measured at
   * 360px, "14 min left" in 13px mono is wide enough to push the badge off the
   * left edge of the bar.
   */
  clockIsTime: boolean;
  /**
   * THE SAME READING, FOR A 64px CIRCLE.
   *
   * The floating queue pill's compact form is a circle with an emoji and about
   * five characters under it. Putting `clockText` in it was tried on a phone on
   * 2026-09-14 and "Ending now" spilled out of both sides and off the edge of
   * the screen.
   *
   * It is a FIELD rather than an abbreviation the pill invents, for the same
   * reason `clockIsTime` is one: two surfaces deciding separately what a phase
   * looks like is how they start disagreeing about which phase it is. Never
   * longer than five characters and never contains a space — asserted.
   */
  compactText: string;
  /** The quiet `role="status"` sentence under the faces, or null. */
  noticeText: string | null;
}

/**
 * The remaining note: "15 min left" down to "1 min left", then m:ss.
 *
 * `Math.ceil`, so the note reads "15 min left" for the whole of the fifteenth
 * minute rather than flicking to 14 one second in — a countdown that rounds
 * down spends its last 59 seconds claiming zero.
 */
function remainingLabel(remainingSeconds: number): string {
  if (remainingSeconds <= 0) return 'Ending now';
  if (remainingSeconds < CAP_SECONDS_THRESHOLD) return mmss(Math.floor(remainingSeconds));

  return `${Math.ceil(remainingSeconds / 60)} min left`;
}

/**
 * The countdown with the words taken out — what a 64px circle can hold.
 *
 * "14 min left" becomes "14m" and "Ending now" becomes "0:00"; under a minute
 * the reading is already a clock and is left alone. Five characters is the
 * budget, measured: the pill's circle is 64px wide with 4px of padding either
 * side, and 10px semibold runs about 6px a character.
 */
function compactRemaining(remainingSeconds: number): string {
  if (remainingSeconds <= 0) return mmss(0);
  if (remainingSeconds < CAP_SECONDS_THRESHOLD) return mmss(Math.floor(remainingSeconds));

  return `${Math.ceil(remainingSeconds / 60)}m`;
}

/**
 * @param elapsedSeconds  seconds since the call began — the count-up.
 * @param remainingSeconds  seconds until the server's `ends_at`, or null when
 *                          the deadline has not reached this client yet (the
 *                          first render before the match response lands, an
 *                          older backend). Null means "count up and say
 *                          nothing", which is exactly the old behaviour.
 * @param capSeconds  how long this call may run in total, as the server armed
 *                    it, or null when that has not arrived either. REQUIRED
 *                    rather than defaulted: a call site that does not pass it
 *                    gets the ninety-minute shape on a three-minute call, which
 *                    is the bug this parameter exists to fix, so the compiler
 *                    asks every caller the question instead of answering it for
 *                    them.
 */
export function callCapView(
  elapsedSeconds: number,
  remainingSeconds: number | null,
  capSeconds: number | null,
): CallCapView {
  const { remainingAt, wrapUpAt } = capThresholds(capSeconds);

  if (remainingSeconds === null || remainingSeconds > remainingAt) {
    const clockText = mmss(Math.max(0, Math.floor(elapsedSeconds)));

    return {
      phase: 'counting-up',
      clockText,
      // While it counts up the reading already IS a clock, so the compact form
      // is the same string. Nothing is abbreviated that did not need to be.
      compactText: clockText,
      clockIsTime: true,
      noticeText: null,
    };
  }

  const label = remainingLabel(remainingSeconds);
  const compactText = compactRemaining(remainingSeconds);
  // Under a minute the label IS a clock again ("0:42"), which is the one case
  // where a countdown wants tabular digits.
  const clockIsTime = remainingSeconds > 0 && remainingSeconds < CAP_SECONDS_THRESHOLD;

  if (remainingSeconds <= 0) {
    return { phase: 'over', clockText: label, compactText, clockIsTime: false, noticeText: null };
  }

  if (remainingSeconds <= wrapUpAt) {
    return {
      phase: 'wrapping-up',
      clockText: label,
      compactText,
      clockIsTime,
      // Computed from the threshold that put us in this phase, never a literal:
      // the two cannot then say different numbers of minutes (the reported bug).
      noticeText: capWrapUpLine(wrapUpAt),
    };
  }

  return { phase: 'remaining', clockText: label, compactText, clockIsTime, noticeText: null };
}

/**
 * Seconds until `endsAt`, from a clock reading. Null when there is no deadline
 * or the one we were given is not a date.
 *
 * The subtraction is here rather than in the component so the "no deadline yet"
 * case has one answer: a NaN leaking into `callCapView` would put the bar into
 * whichever branch NaN comparisons happen to fall through.
 */
export function capRemainingSeconds(endsAt: string | null, nowMs: number): number | null {
  if (!endsAt) return null;
  const deadline = Date.parse(endsAt);
  if (!Number.isFinite(deadline)) return null;

  return Math.round((deadline - nowMs) / 1000);
}

/** Has this client waited long enough past the deadline to end the call itself? */
export function capOverrunLocally(remainingSeconds: number | null): boolean {
  return remainingSeconds !== null && remainingSeconds <= -CAP_LOCAL_FALLBACK_SECONDS;
}

/**
 * WHO OWNS THE EXIT once the cap has fired.
 *
 * When the server ends a call at its deadline, FIVE things try to end it in the
 * same second: the PeerConnection closing as the call tears down, the status
 * poll finding no active session, the ACTIVE render branch losing its session
 * id, the visibility handler on the way back to the tab, and the ordinary
 * partner-left detection. Each of them reaches the flow's `handleEndCall`,
 * which would put the feedback form up over the "Time's up" screen before
 * anybody had read it.
 *
 * Guarding those five call sites would be five guards for two flows to keep in
 * step — the exact shape class D describes, and the shape that takes N review
 * rounds to converge. One predicate, read once inside the function they all
 * reach, cannot be forgotten at a sixth call site.
 */
export interface CallEndingOwnership {
  /** The cap fired; the "Time's up" screen is up or about to be. */
  timeLimitReached: boolean;
  /** The screen has finished — by its timer or by Continue. */
  timeUpAcknowledged: boolean;
}

export function mayRunOrdinaryEnd({
  timeLimitReached,
  timeUpAcknowledged,
}: CallEndingOwnership): boolean {
  return !timeLimitReached || timeUpAcknowledged;
}
